function toIsoZ(ms) {
  return new Date(ms).toISOString().slice(0, 19) + 'Z'
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

// "YYYY-MM-DD" včerejšího dne (lokální datum prohlížeče). DENNÍ data (DLY) z dnešního
// dne nemá smysl stahovat — den ještě neskončil, hodnoty nejsou kompletní. Horní mez
// pro denní data / denní časové řady je proto vždy včerejšek.
// (Netýká se hodinových HLY ani 10minutových 10M dat — tam se dnešek stahuje běžně.)
export function yesterdayStr() {
  const n = new Date()
  n.setDate(n.getDate() - 1)
  return `${n.getFullYear()}-${pad2(n.getMonth() + 1)}-${pad2(n.getDate())}`
}

// Offset in ms for a UTC date in the given timezone — per-timestamp, DST-aware.
// Uses Intl.DateTimeFormat.formatToParts + Date.UTC to avoid new Date(string) ambiguity
// on DST fall-back days (where e.g. "2:00 AM" exists twice in the local timezone).
export function getOffsetMs(utcDate, tz) {
  if (tz === 'UTC') return 0
  if (tz === 'CET') return 60 * 60 * 1000
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: 'Europe/Prague',
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', second: 'numeric',
    hour12: false,
  }).formatToParts(utcDate)
  const get = t => parseInt(parts.find(p => p.type === t)?.value ?? '0')
  return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second')) - utcDate.getTime()
}

// UTC begin/end that cover the full local day (00:00 – endHour:endMinute).
export function localDayApiRange(dateStr, tz, endHour = 23, endMinute = 0) {
  if (tz === 'UTC') {
    const hh = String(endHour).padStart(2, '0')
    const mm = String(endMinute).padStart(2, '0')
    return { begin_date: `${dateStr}T00:00:00Z`, end_date: `${dateStr}T${hh}:${mm}:00Z` }
  }
  const [y, m, d] = dateStr.split('-').map(Number)
  const utcMidnightMs = Date.UTC(y, m - 1, d, 0, 0, 0)
  const utcEndMs      = Date.UTC(y, m - 1, d, endHour, endMinute, 0)
  return {
    begin_date: toIsoZ(utcMidnightMs - getOffsetMs(new Date(utcMidnightMs), tz)),
    end_date:   toIsoZ(utcEndMs      - getOffsetMs(new Date(utcEndMs),      tz)),
  }
}

// UTC begin/end that cover the full local month.
export function localMonthApiRange(year, month, tz) {
  if (tz === 'UTC') {
    const mm      = String(month).padStart(2, '0')
    const lastDay = String(new Date(year, month, 0).getDate()).padStart(2, '0')
    return { begin_date: `${year}-${mm}-01T00:00:00Z`, end_date: `${year}-${mm}-${lastDay}T23:00:00Z` }
  }
  const utcFirstMs  = Date.UTC(year, month - 1, 1, 0, 0, 0)
  const lastDay     = new Date(year, month, 0).getDate()
  const utcLastMs   = Date.UTC(year, month - 1, lastDay, 23, 0, 0)
  return {
    begin_date: toIsoZ(utcFirstMs - getOffsetMs(new Date(utcFirstMs), tz)),
    end_date:   toIsoZ(utcLastMs  - getOffsetMs(new Date(utcLastMs),  tz)),
  }
}

// Local display label "HH:mm" for a UTC Date using per-timestamp offset.
export function localHourMinuteLabel(utcDate, tz) {
  const local = new Date(utcDate.getTime() + getOffsetMs(utcDate, tz))
  return `${String(local.getUTCHours()).padStart(2, '0')}:${String(local.getUTCMinutes()).padStart(2, '0')}`
}

// Local display label "HH:00" for a UTC Date using per-timestamp offset.
export function localHourLabel(utcDate, tz) {
  const local = new Date(utcDate.getTime() + getOffsetMs(utcDate, tz))
  return `${String(local.getUTCHours()).padStart(2, '0')}:00`
}

// Local "YYYY-MM-DD HH" key for HlyGrid lookup using per-timestamp offset.
export function localHlyKey(utcDate, tz) {
  const local = new Date(utcDate.getTime() + getOffsetMs(utcDate, tz))
  return `${local.getUTCFullYear()}-${String(local.getUTCMonth() + 1).padStart(2, '0')}-${String(local.getUTCDate()).padStart(2, '0')} ${String(local.getUTCHours()).padStart(2, '0')}`
}
